import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown, FiHelpCircle, FiSettings, FiUsers, FiStar, FiHeadphones } from 'react-icons/fi';
import Icon from '../Icon/Icon';
import AnimatedCard from '../AnimatedCard/AnimatedCard';

const FAQSection = styled.main`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
`;

const HeroSection = styled.div`
  padding: ${({ theme }) => `${theme.spacing.xxl} ${theme.spacing.md} ${theme.spacing.xl}`};
  text-align: center;
  position: relative;
  overflow: hidden;
  
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => `${theme.spacing.xl} ${theme.spacing.sm} ${theme.spacing.lg}`};
  }
`;

const Container = styled.div`
  max-width: ${({ theme }) => theme.breakpoints.desktop};
  margin: 0 auto;
  width: 100%;
  position: relative;
  z-index: 10;
`;

const Title = styled(motion.h1)`
  font-size: clamp(2.5rem, 6vw, 4rem);
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
`;

const Subtitle = styled(motion.p)`
  font-size: clamp(1.1rem, 2.5vw, 1.3rem);
  color: ${({ theme }) => theme.colors.primary};
  opacity: 0.8;
  margin-bottom: ${({ theme }) => theme.spacing.xxl};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;


const ContentSection = styled.div`
  padding: 0 ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xxl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 0 ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.xl};
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xxl};
  align-items: stretch; /* This ensures all grid items have the same height */
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.lg};
  }
`;

const CategoryCard = styled(AnimatedCard)`
  height: 100%; /* This makes each card fill the full height of its grid cell */
  display: flex;
  flex-direction: column;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.md};
  border-bottom: 2px solid ${({ theme }) => `${theme.colors.primary}15`};
`;

const CategoryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.accent};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.background};
  font-size: 1.5rem;
`;

const CategoryTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
`;

const CategoryDescription = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.primary};
  opacity: 0.7;
  margin: 0;
  line-height: 1.4;
`;

const FAQItemsContainer = styled.div`
  flex: 1; /* This makes the FAQ items section grow to fill remaining space */
  display: flex;
  flex-direction: column;
`;

const FAQItem = styled(motion.div)`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => `${theme.colors.primary}15`};
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    border-color: ${({ theme }) => `${theme.colors.primary}30`};
    box-shadow: ${({ theme }) => theme.shadows.small};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const QuestionButton = styled(motion.button)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.lg};
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    background: ${({ theme }) => `${theme.colors.primary}05`};
  }
`;

const QuestionText = styled.span`
  flex: 1;
  margin-right: ${({ theme }) => theme.spacing.md};
`;

const ChevronIcon = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.colors.accent};
`;

const Answer = styled(motion.div)`
  padding: ${({ theme }) => `0 ${theme.spacing.lg} ${theme.spacing.lg}`};
  color: ${({ theme }) => theme.colors.primary};
  opacity: 0.8;
  line-height: 1.6;
  font-size: 0.95rem;
`;

const ContactSection = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  background: ${({ theme }) => `${theme.colors.primary}08`};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  margin: ${({ theme }) => theme.spacing.xxl} 0;
`;

const ContactTitle = styled.h3`
  font-size: clamp(1.5rem, 3vw, 2rem);
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-weight: 600;
`;

const ContactText = styled.p`
  color: ${({ theme }) => theme.colors.primary};
  opacity: 0.8;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  font-size: 1.1rem;
  line-height: 1.6;
`;

const ContactButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.xl}`};
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.background};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  box-shadow: ${({ theme }) => theme.shadows.medium};

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.large};
  }

  &:active {
    transform: translateY(0);
  }
`;

interface FAQItemData {
  question: string;
  answer: string;
}

interface FAQCategory {
  title: string;
  description: string;
  icon: any;
  items: FAQItemData[];
}

const faqData: FAQCategory[] = [
  {
    title: "General Questions",
    description: "Basic information about Radyofone and how it works",
    icon: FiHelpCircle,
    items: [
      {
        question: "What is Radyofone?",
        answer: "Radyofone is a modern online radio platform where you can stream music, discover stations by mood or genre, and enjoy curated playlists 24/7."
      },
      {
        question: "Is Radyofone free to use?",
        answer: "Yes, Radyofone is free to use with ads. We also plan to introduce a premium option for an ad-free experience in the future."
      }
    ]
  },
  {
    title: "Technical Support",
    description: "Help with app functionality and troubleshooting",
    icon: FiSettings,
    items: [
      {
        question: "Do I need to download the app to listen?",
        answer: "Yes, you should download the app."
      },
      {
        question: "Can I use Radyofone offline?",
        answer: "Currently, Radyofone works only with an internet connection. Offline listening is not available."
      },
      {
        question: "On which devices can I use Radyofone?",
        answer: "You can use Radyofone on any Android or iOS device."
      }
    ]
  },
  {
    title: "Content",
    description: "Information about music and radio content",
    icon: FiStar,
    items: [
      {
        question: "What kind of music can I find on Radyofone?",
        answer: "We offer a wide range of music, from international hits to Arabic classics, categorized by genre, mood, and location."
      },
      {
        question: "Do you broadcast live radio shows?",
        answer: "Yes, we feature live shows and scheduled programs, which you can follow through the app and website."
      },
      {
        question: "Can I request a song?",
        answer: "Yes! You can use our \"Request a Song\" feature inside the app."
      }
    ]
  },
  {
    title: "Account & Personalization",
    description: "Managing your account and customizing your experience",
    icon: FiUsers,
    items: [
      {
        question: "Do I need to create an account to listen?",
        answer: "No, you can access the app as a guest without registration; however, guest access is limited. For better security and to receive notifications about new songs and new features, we recommend creating an account."
      },
      {
        question: "How can I create a favorites list?",
        answer: "Just tap the heart icon next to any station, and it will be added to your favorites."
      }
    ]
  },
  {
    title: "Ads & Premium",
    description: "Information about advertising and premium features",
    icon: FiStar,
    items: [
      {
        question: "Why do I hear ads while listening?",
        answer: "Ads help us keep Radyofone free for all users."
      },
      {
        question: "Will you offer an ad-free option?",
        answer: "Yes, we plan to introduce a premium subscription in the future for an ad-free experience and exclusive stations."
      }
    ]
  },
  {
    title: "Support",
    description: "Getting help and contacting our team",
    icon: FiHeadphones,
    items: [
      {
        question: "I can't play a station, what should I do?",
        answer: "Try refreshing the app. If the issue continues, check your internet connection or contact our support team."
      },
      {
        question: "How can I contact support?",
        answer: "You can reach us anytime through our contact form or WhatsApp support available on this website."
      }
    ]
  }
];

const FAQ: React.FC = () => {
  const [openItems, setOpenItems] = useState<{ [key: string]: boolean }>({});

  const toggleItem = (categoryIndex: number, itemIndex: number) => {
    const key = `${categoryIndex}-${itemIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const heroVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut" as const
      }
    }
  };

  return (
    <FAQSection>
      <HeroSection>
        <Container>
          <Title
            variants={heroVariants}
            initial="hidden"
            animate="visible"
          >
            Frequently Asked Questions
          </Title>
          <Subtitle
            variants={heroVariants}
            initial="hidden"
            animate="visible"
            style={{ transitionDelay: '0.2s' }}
          >
            Find answers to common questions about Radyofone. Can't find what you're looking for? Our support team is here to help!
          </Subtitle>

        </Container>
      </HeroSection>

      <ContentSection>
        <Container>
          <CategoriesGrid>
            {faqData.map((category, categoryIndex) => (
              <CategoryCard key={categoryIndex} delay={categoryIndex * 0.1}>
                <CategoryHeader>
                  <CategoryIcon>
                    <Icon icon={category.icon} size={24} />
                  </CategoryIcon>
                  <div>
                    <CategoryTitle>{category.title}</CategoryTitle>
                    <CategoryDescription>{category.description}</CategoryDescription>
                  </div>
                </CategoryHeader>

                <FAQItemsContainer>
                  {category.items.map((item, itemIndex) => {
                    const isOpen = openItems[`${categoryIndex}-${itemIndex}`];

                    return (
                      <FAQItem key={itemIndex}>
                        <QuestionButton
                          onClick={() => toggleItem(categoryIndex, itemIndex)}
                          whileHover={{ backgroundColor: 'rgba(38, 108, 127, 0.05)' }}
                          whileTap={{ scale: 0.995 }}
                        >
                          <QuestionText>{item.question}</QuestionText>
                          <ChevronIcon
                            animate={{ rotate: isOpen ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <Icon icon={FiChevronDown} size={20} />
                          </ChevronIcon>
                        </QuestionButton>

                        <AnimatePresence>
                          {isOpen && (
                            <Answer
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3, ease: "easeInOut" as const }}
                            >
                              {item.answer}
                            </Answer>
                          )}
                        </AnimatePresence>
                      </FAQItem>
                    );
                  })}
                </FAQItemsContainer>
              </CategoryCard>
            ))}
          </CategoriesGrid>

          <AnimatedCard delay={0.8}>
            <ContactSection>
              <ContactTitle>Still have questions?</ContactTitle>
              <ContactText>
                Our friendly support team is here to help you get the most out of Radyofone.
                Reach out anytime and we'll get back to you quickly!
              </ContactText>
              <ContactButton
                onClick={() => window.location.href = '/contact'}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Icon icon={FiHeadphones} size={20} />
                Contact Support
              </ContactButton>
            </ContactSection>
          </AnimatedCard>
        </Container>
      </ContentSection>
    </FAQSection>
  );
};

export default FAQ;